<script setup>
  import {computed, ref, watch} from "vue";

  import ComponentCategoryBadge from "@components/Badges/CategoryBadge.vue";
  import ComponentContactCard from "@components/Cards/ContactCard.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentEmptyState from "@components/EmptyState/EmptyState.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import {MedButton, MedInfoButton, MedSpinner} from "@pharma4u/medicheck-components";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "ComponentPatientContactSelectDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["contactSelected"]);

  const refPatientContactSelectDialog = ref(null);

  const addressBook = ref([]);
  const contactSearch = useAuthHttp();

  const addressBookSearch = ref(null);
  const selectedContact = ref(null);

  const filteredAddressBook = computed(() => {
    if (addressBookSearch.value === null || addressBookSearch.value.length === 0) {
      return addressBook.value;
    }

    return (
      addressBook.value.filter((contact) => {
        let searchString = [contact.firstname, contact.lastname, contact.company].join(" ").toLowerCase();
        return addressBookSearch.value
          .toLowerCase()
          .split(" ")
          .every((word) => searchString.includes(word));
      }) ?? []
    );
  });

  const showNoContactsFound = computed(() => {
    return filteredAddressBook.value?.length === 0 && !contactSearch.processing;
  });

  watch(addressBookSearch, (newValue) => {
    // select first search result when searching in addressbook
    if (newValue === null || newValue.length === 0) {
      selectedContact.value = null;
    } else {
      selectedContact.value = filteredAddressBook.value[0];
    }
  });

  const isSelectedContact = (contact) => {
    return selectedContact.value !== null && selectedContact.value?.id === contact.id;
  };

  const open = (search = null) => {
    contactSearch.post(route("api.contacts.search"), {
      onSuccess: (response) => {
        addressBook.value = response?.data ?? [];

        if (typeof search === "string") {
          addressBookSearch.value = search.replace("Dr.", "").replace("Prof.", "").replace("Med.", "").trim();
        } else if (typeof search === "object" && search !== null) {
          addressBookSearch.value = [search.firstname ?? null, search.lastname ?? null]
            .filter((p) => {
              return p !== null;
            })
            .join(" ");
        } else if (search === null && favoriteContactId.value) {
          selectedContact.value = addressBook.value.find((contact) => contact.id === favoriteContactId.value);
        }
      },
    });

    refPatientContactSelectDialog.value.open();
  };

  const favoriteContactId = computed(() => {
    return props.patient.contacts.find((contact) => contact.favorite)?.id;
  });

  const resetDialog = () => {
    selectedContact.value = null;
    addressBookSearch.value = null;
  };

  const closeDialog = () => {
    refPatientContactSelectDialog.value.close();
    resetDialog();
  };

  const confirm = () => {
    emit("contactSelected", selectedContact.value);
    closeDialog();
  };

  defineExpose({
    open,
    closeDialog,
    resetDialog,
  });
</script>

<template>
  <ComponentDialog
    ref="refPatientContactSelectDialog"
    dialog-width="medium"
    dialog-type="emptyActions"
    dialog-title="Kontakt einfügen"
    primary-label="Hinzufügen"
    :test-id="getTestId('patient-contact')"
  >
    <template #content>
      <div v-if="contactSearch.processing" class="grid min-h-32 w-full place-content-center">
        <MedSpinner size="xl" :data-test="getTestId('patient-contact-dialog-spinner')" />
      </div>

      <div v-else class="grid grid-cols-6">
        <div class="col-span-2">
          <div>
            <ComponentInput
              v-model="addressBookSearch"
              autocomplete="off"
              class="max-w-52"
              placeholder="Suchen"
              :test-id="getTestId('patient-contact-dialog-search')"
            >
              <template #icon>
                <ComponentIcon class="text-xl text-gray-600">search</ComponentIcon>
              </template>
            </ComponentInput>

            <div class="h-72 overflow-x-hidden overflow-y-auto overscroll-contain p-2">
              <ul v-if="filteredAddressBook?.length > 0" class="space-y-2">
                <li
                  v-for="contact in filteredAddressBook"
                  :key="contact.id"
                  class="group rounded-md px-2 py-1 hover:bg-gray-100"
                  :class="{'bg-gray-100': isSelectedContact(contact)}"
                >
                  <button
                    class="flex w-full items-center justify-between truncate"
                    :data-test="getTestId('patient-contact-dialog-select-button')"
                    @focus="selectedContact = contact"
                    @mouseover="selectedContact = contact"
                    @click="selectedContact = contact"
                  >
                    <span class="flex flex-col items-start space-y-0.5 text-left">
                      <span v-if="contact.firstname || contact.lastname">
                        {{ contact.title }} {{ contact.firstname }} {{ contact.lastname }}
                      </span>

                      <span v-else>{{ contact.company }}</span>

                      <span class="flex space-x-1">
                        <ComponentCategoryBadge v-if="contact.id === favoriteContactId" color="red">
                          <ComponentIcon
                            v-if="contact.id === favoriteContactId"
                            class="inline-block text-base text-mcred"
                          >
                            bookmark
                          </ComponentIcon>
                          Favorit
                        </ComponentCategoryBadge>

                        <ComponentCategoryBadge
                          v-if="patient?.contacts?.some((pC) => pC.id === contact.id)"
                          color="green"
                          label="Verknüpft"
                        />
                      </span>
                    </span>

                    <ComponentIcon
                      class="hidden text-gray-600 group-hover:block"
                      :class="{'block!': isSelectedContact(contact)}"
                    >
                      chevron_right
                    </ComponentIcon>
                  </button>
                </li>
              </ul>

              <div v-else-if="showNoContactsFound">
                <ComponentEmptyState class="mb-5" :data-test="getTestId('patient-contact-dialog')">
                  Keine Kontakte gefunden.
                </ComponentEmptyState>
              </div>
            </div>
          </div>
        </div>

        <div class="col-span-4 px-4">
          <div v-if="selectedContact">
            <ComponentContactCard :contact="selectedContact" />
          </div>
        </div>
      </div>
    </template>

    <template #actions>
      <div class="mt-4 flex w-full flex-row-reverse flex-wrap justify-between">
        <div class="flex flex-row-reverse space-x-3 space-x-reverse">
          <MedButton
            variant="primary"
            :disabled="showNoContactsFound || selectedContact === null"
            :data-test="getTestId('patient-contact-dialog-confirm-button')"
            @click="confirm()"
          >
            Einfügen
          </MedButton>

          <MedButton :data-test="getTestId('patient-contact-dialog-close-button')" @click="closeDialog()">
            Schließen
          </MedButton>
        </div>
      </div>
    </template>

    <template #info-icon>
      <MedInfoButton
        size="lg"
        side="top"
        aria-label="Kontakt Info"
        disable-portal
        :data-test="getTestId('patient-contact-dialog-info-button')"
      >
        <p class="text-sm">
          Stellen Sie eine Verbindung zwischen dem Patienten und Ärzten, Pflegediensten oder Heimen aus Ihrem Adressbuch
          her.
        </p>
      </MedInfoButton>
    </template>
  </ComponentDialog>
</template>
